import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../hooks/useNotification';
import { PROJECT_INSTRUCTIONS_MAX_LENGTH } from '../constants';
import { useProjects } from '../hooks/useProjects';
import type { Project } from '../types';

interface ProjectInstructionsPanelProps {
  project: Project;
}

export const ProjectInstructionsPanel: React.FC<ProjectInstructionsPanelProps> = ({ project }) => {
  const { t } = useTranslation();
  const { success, error: showError } = useNotification();
  const { updateProject } = useProjects();
  const saved = project.instructions ?? '';
  const [value, setValue] = useState(saved);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = value.trim() !== saved.trim();

  useEffect(() => {
    setValue(saved);
  }, [saved, project.publicId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateProject(project.publicId, { instructions: value });
      if (response.success) {
        success(t('projects.instructions.saved'));
      } else {
        showError(response.error ?? t('projects.form.submitFailed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <p className="text-sm text-gray-600 dark:text-slate-400">{t('projects.instructions.hint')}</p>
      <TextField
        fullWidth
        multiline
        minRows={6}
        maxRows={16}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('projects.instructions.placeholder')}
        inputProps={{ maxLength: PROJECT_INSTRUCTIONS_MAX_LENGTH }}
        helperText={`${value.length} / ${PROJECT_INSTRUCTIONS_MAX_LENGTH}`}
      />
      <div className="flex justify-end">
        <Button
          variant="contained"
          size="small"
          disabled={!isDirty || isSaving}
          onClick={() => void handleSave()}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('projects.form.save')}
        </Button>
      </div>
    </div>
  );
};
